/**
 * usePatient360 - Custom Hook for Patient 360 Data Fetching
 * 
 * Provides centralized data fetching with:
 * - Type-safe responses
 * - Proper error handling
 * - Optimized caching (5 minute staleTime)
 * - Automatic refetch on parameter changes
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Patient360API } from '../services/patient360Service';
import type { Patient360ResponseDto } from '../types/patient360';
import {
  normalizePatient360,
  validatePatient360,
  type Patient360ViewModel,
} from '../types/patient360Model';

// ─── Cache Configuration ────────────────────────────────────

const CACHE_CONFIG = {
  /** Patient 360 data - 5 minute stale time (frequently accessed) */
  PATIENT_360_STALE_TIME: 5 * 60 * 1000,
  /** Tab-specific data - 2 minute stale time */
  TAB_DATA_STALE_TIME: 2 * 60 * 1000,
  /** Background refetch - 10 minutes */
  REFETCH_INTERVAL: 10 * 60 * 1000,
};

// ─── Query Keys ──────────────────────────────────────────────

export const patient360QueryKeys = {
  all: ['patient360'] as const,
  detail: (patientId: string) => [...patient360QueryKeys.all, patientId] as const,
  patient: (programId: string, patientId: string) => [...patient360QueryKeys.detail(patientId), 'patient', programId] as const,
  cases: (patientId: string) => [...patient360QueryKeys.detail(patientId), 'cases'] as const,
  timeline: (patientId: string) => [...patient360QueryKeys.detail(patientId), 'timeline'] as const,
  documents: (patientId: string) => [...patient360QueryKeys.detail(patientId), 'documents'] as const,
  financials: (patientId: string) => [...patient360QueryKeys.detail(patientId), 'financials'] as const,
};

// ─── Error Type ──────────────────────────────────────────────

export interface Patient360Error {
  message: string;
  correlationId?: string;
  statusCode?: number;
  details?: string;
}

/**
 * Extract error from API response or Error object
 * Handles Axios errors, API response errors, and generic errors
 */
export function extractPatient360Error(error: unknown): Patient360Error {
  // Handle null/undefined
  if (!error) {
    return { message: 'An unknown error occurred' };
  }

  // Handle Axios error with response
  if (typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: {
        data?: { message?: string; correlationId?: string; success?: boolean; errors?: string[] };
        status?: number;
        statusText?: string;
      };
      message?: string;
    };
    
    if (axiosError.response) {
      const { data, status, statusText } = axiosError.response;
      
      // API returned structured error response
      if (data?.message) {
        return {
          message: data.message,
          correlationId: data.correlationId,
          statusCode: status,
          details: data.errors?.join(', '),
        };
      }
      
      // HTTP status-based messages
      if (status === 403) {
        return {
          message: 'Access denied. You do not have permission to view this patient.',
          statusCode: status,
        };
      }
      if (status === 404) {
        return {
          message: 'Patient not found.',
          statusCode: status,
        };
      }
      if (status === 500) {
        return {
          message: 'Server error. Please try again later.',
          statusCode: status,
        };
      }
      
      return {
        message: statusText || axiosError.message || 'Request failed',
        statusCode: status,
      };
    }
    
    // Network error (no response)
    if (axiosError.message) {
      if (axiosError.message.includes('Network Error')) {
        return { message: 'Unable to connect to server. Please check your network connection.' };
      }
      return { message: axiosError.message };
    }
  }

  // Handle standard Error object
  if (error instanceof Error) {
    return { message: error.message };
  }

  // Handle string error
  if (typeof error === 'string') {
    return { message: error };
  }

  return { message: 'An unexpected error occurred' };
}

// ─── Main Hook: Full Patient 360 Data ────────────────────────

export interface UsePatient360Options {
  enabled?: boolean;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
}

export function usePatient360(
  patientId: string | undefined,
  programId: string | undefined,
  options?: UsePatient360Options
) {
  const { enabled = true, staleTime = CACHE_CONFIG.PATIENT_360_STALE_TIME, refetchOnWindowFocus = false } = options || {};

  return useQuery({
    queryKey: patient360QueryKeys.detail(patientId!),
    queryFn: () => Patient360API.getPatient360(patientId!),
    enabled: enabled && !!patientId && !!programId,
    staleTime,
    refetchOnWindowFocus,
    placeholderData: (prev) => prev,
    select: (response) => response.data,
  });
}

// ─── Tab-Specific Hooks ──────────────────────────────────────

/**
 * Hook for Cases tab data
 */
export function usePatient360Cases(
  patientId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: patient360QueryKeys.cases(patientId!),
    queryFn: () => Patient360API.getCases(patientId!),
    enabled: options?.enabled !== false && !!patientId,
    staleTime: CACHE_CONFIG.TAB_DATA_STALE_TIME,
    select: (response) => response.data,
  });
}

/**
 * Hook for Timeline tab data
 */
export function usePatient360Timeline(
  patientId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: patient360QueryKeys.timeline(patientId!),
    queryFn: () => Patient360API.getTimeline(patientId!),
    enabled: options?.enabled !== false && !!patientId,
    staleTime: CACHE_CONFIG.TAB_DATA_STALE_TIME,
    select: (response) => response.data,
  });
}

/**
 * Hook for Documents tab data
 */
export function usePatient360Documents(
  patientId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: patient360QueryKeys.documents(patientId!),
    queryFn: () => Patient360API.getDocuments(patientId!),
    enabled: options?.enabled !== false && !!patientId,
    staleTime: CACHE_CONFIG.TAB_DATA_STALE_TIME,
    select: (response) => response.data,
  });
}

/**
 * Hook for Financials tab data
 */
export function usePatient360Financials(
  patientId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: patient360QueryKeys.financials(patientId!),
    queryFn: () => Patient360API.getFinancials(patientId!),
    enabled: options?.enabled !== false && !!patientId,
    staleTime: CACHE_CONFIG.TAB_DATA_STALE_TIME,
    select: (response) => response.data,
  });
}

// ─── Combined Hook with Derived State ────────────────────────

export interface Patient360State {
  data: Patient360ResponseDto | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Patient360Error | null;
  refetch: () => void;
}

/**
 * Comprehensive Patient360 hook with error state management
 */
export function usePatient360WithState(
  patientId: string | undefined,
  programId: string | undefined,
  options?: UsePatient360Options
): Patient360State {
  const query = usePatient360(patientId, programId, options);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ? extractPatient360Error(query.error) : null,
    refetch: query.refetch,
  };
}

// ─── Normalized ViewModel Hook ────────────────────────────────

export interface Patient360ViewModelState {
  /** Normalized, UI-safe view model. Undefined while loading. */
  viewModel: Patient360ViewModel | undefined;
  isLoading: boolean;
  /** True when refetching with existing data (background refresh). */
  isFetching: boolean;
  /** True when showing cached data from a previous queryKey (patient switch). */
  isPlaceholderData: boolean;
  isError: boolean;
  error: Patient360Error | null;
  refetch: () => void;
}

/**
 * Preferred hook for Patient 360 page. Returns a normalized Patient360ViewModel
 * that enforces the data contract — components should never need raw API fields.
 *
 * Automatically logs validation warnings (missing name, $0 assistance, etc.)
 * to the browser console in development, aiding cross-program debugging.
 */
export function usePatient360ViewModel(
  patientId: string | undefined,
  programId: string | undefined,
  options?: UsePatient360Options
): Patient360ViewModelState {
  const query = usePatient360(patientId, programId, options);

  const viewModel = useMemo<Patient360ViewModel | undefined>(() => {
    if (!query.data) return undefined;
    const vm = normalizePatient360(query.data);
    if (import.meta.env.DEV) {
      const { warnings } = validatePatient360(vm);
      if (warnings.length > 0) {
        console.warn(`[Patient360] Data validation warnings for patient ${patientId}:`, warnings);
      }
    }
    return vm;
  }, [query.data, patientId]);

  return {
    viewModel,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error ? extractPatient360Error(query.error) : null,
    refetch: query.refetch,
  };
}
